/**
 * useObserver — agent observation system.
 *
 * Watches group chat activity for clinically significant events,
 * dispatches context digests to specialist agents, routes suggestions
 * through Casey for review, and surfaces approved suggestions to the user.
 *
 * Ported from JSX codebase (master:frontend/src/hooks/useObserver.js)
 * to TypeScript. The JSX version depended on a separate `observerService`
 * module for buffering, debouncing, and cooldown tracking. This TS port
 * inlines a lightweight version of that logic since the observer service
 * files were not ported (they are tightly coupled to the Node.js backend).
 *
 * The observer operates in three phases:
 *   1. Record events — buffer clinically significant happenings
 *   2. Dispatch — when buffer threshold or debounce timer fires,
 *      build a digest and send to specialist agents
 *   3. Casey review — specialist suggestions are routed through Casey
 *      for approval before being surfaced to the physician
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import type { CanvasTab, UseCanvasReturn } from './useCanvas'
import type { AgentToolCall } from '../contexts/ChatContext'
import type { GroupEvent, ObservableEvent } from './useOrchestration'
import { AGENT_LABELS } from '../utils/agentUtils'
import { stripHtml } from '../utils/domUtils'

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

interface PendingSuggestion {
  tab: CanvasTab
  status: 'pending' | 'accepted' | 'rejected'
}

interface CooldownEntry {
  agentId: string
  until: number
}

interface UseObserverOptions {
  workspaceId: string | null
  canvas: UseCanvasReturn
  sendToAgent: (agentId: string, message: string, mode?: string, canvasSnapshots?: Record<string, string>, options?: { orchestrate?: boolean }) => Promise<{ output: string; tools: AgentToolCall[] } | null>
  addGroupEvent?: (event: GroupEvent) => void
  bufferThreshold?: number
  debounceMs?: number
  cooldownMs?: number
}

export interface UseObserverReturn {
  recordEvent: (event: ObservableEvent) => void
  triggerManual: () => void
  pendingSuggestions: Record<string, PendingSuggestion>
  clearPendingSuggestion: (agentId: string) => void
  isDispatching: boolean
  bufferSize: number
}

// ────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────

// stripHtml imported from ../utils/domUtils

/** Build a text digest from buffered events */
const buildDigest = (events: ObservableEvent[]): string => {
  if (events.length === 0) return ''
  const lines = events.map(e => `- [${e.type}] ${e.title}${e.detail ? `: ${e.detail}` : ''}`)
  return `The following clinical events have occurred recently:\n\n${lines.join('\n')}`
}

/** Determine target agents for an observation dispatch */
const selectTargetAgents = (events: ObservableEvent[]): string[] => {
  const agents = new Set<string>()

  for (const event of events) {
    if (event.type === 'canvas_update' || event.type === 'user_edit') {
      // Canvas changes may affect both specialists
      agents.add('hpi-pe-agent')
      agents.add('clinical-reasoning')
    } else if (event.type === 'tool_completion') {
      // Tool completions may affect the other specialist
      if (event.agentId === 'hpi-pe-agent') agents.add('clinical-reasoning')
      else if (event.agentId === 'clinical-reasoning') agents.add('hpi-pe-agent')
      else {
        agents.add('hpi-pe-agent')
        agents.add('clinical-reasoning')
      }
    } else if (event.type === 'file_upload') {
      agents.add('hpi-pe-agent')
      agents.add('clinical-reasoning')
    }
  }

  return Array.from(agents)
}

// ────────────────────────────────────────────────────────
// Hook
// ────────────────────────────────────────────────────────

export function useObserver({
  workspaceId,
  canvas,
  sendToAgent,
  addGroupEvent: emitGroupEvent,
  bufferThreshold = 3,
  debounceMs = 10000,
  cooldownMs = 30000,
}: UseObserverOptions): UseObserverReturn {

  const [pendingSuggestions, setPendingSuggestions] = useState<Record<string, PendingSuggestion>>({})
  const [isDispatching, setIsDispatching] = useState(false)

  // Event buffer
  const eventBuffer = useRef<ObservableEvent[]>([])
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cooldowns = useRef<CooldownEntry[]>([])
  const canvasRef = useRef(canvas)
  canvasRef.current = canvas

  // Clear all observer state on workspace switch to prevent cross-patient contamination
  const prevWorkspaceRef = useRef(workspaceId)
  useEffect(() => {
    if (prevWorkspaceRef.current === workspaceId) return
    prevWorkspaceRef.current = workspaceId
    // Clear event buffer
    eventBuffer.current = []
    // Clear debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
      debounceTimer.current = null
    }
    // Clear cooldowns
    cooldowns.current = []
    // Clear pending suggestions
    setPendingSuggestions({})
    setIsDispatching(false)
  }, [workspaceId])

  const emitEvent = useCallback((event: GroupEvent) => {
    emitGroupEvent?.(event)
  }, [emitGroupEvent])

  // Single enforcement point: every observer turn is background traffic and
  // routes through this wrapper, so orchestrate:false is structural — a stray
  // relay marker must never grow a consult pill in the shared visible session,
  // and a future call site gets the safe behavior without remembering the tail.
  const sendBackground = useCallback((
    agentId: string,
    message: string
  ) => sendToAgent(agentId, message, undefined, undefined, { orchestrate: false }),
  [sendToAgent])

  /** Get canvas content as plain text snapshots */
  const getCanvasSnapshots = useCallback((): Record<string, string> => {
    const snapshots = canvasRef.current.getCanvasSnapshots()
    return {
      hpi: snapshots.hpi,
      pe: snapshots.pe,
      ap: snapshots.assessment_and_plan,
    }
  }, [])

  /** Check if an agent is on cooldown */
  const isOnCooldown = useCallback((agentId: string): boolean => {
    const now = Date.now()
    return cooldowns.current.some(c => c.agentId === agentId && c.until > now)
  }, [])

  /** Set cooldown for an agent */
  const setCooldown = useCallback((agentId: string) => {
    cooldowns.current = [
      ...cooldowns.current.filter(c => c.agentId !== agentId),
      { agentId, until: Date.now() + cooldownMs },
    ]
  }, [cooldownMs])

  /**
   * Main dispatch pipeline: digest -> specialist -> Casey review -> user.
   */
  const dispatchObservations = useCallback(async (events: ObservableEvent[]) => {
    if (events.length === 0) return

    const targetAgents = selectTargetAgents(events)
      .filter(a => !isOnCooldown(a))

    if (targetAgents.length === 0) return

    const digest = buildDigest(events)
    const snapshots = getCanvasSnapshots()
    const contextBlock =
      `\n\nCurrent canvas state:\n` +
      `HPI: ${snapshots.hpi || '(empty)'}\n` +
      `PE: ${snapshots.pe || '(empty)'}\n` +
      `A&P: ${snapshots.ap || '(empty)'}`

    setIsDispatching(true)

    emitEvent({
      type: 'observation_dispatched',
      agentId: 'system',
      title: `Observation digest sent to ${targetAgents.length} agent${targetAgents.length > 1 ? 's' : ''}`,
    })

    for (const agentId of targetAgents) {
      const tabScope = agentId === 'hpi-pe-agent' ? 'HPI and PE' : 'A&P'
      const observationMsg =
        `[OBSERVATION] Scope: ${tabScope}\n\n${digest}${contextBlock}\n\n` +
        `Review the above events and current canvas state. If changes are needed to your section, ` +
        `respond with [SUGGEST_EDIT:tab] followed by the complete updated content. ` +
        `If no changes are needed, respond with [NO_CHANGES].`

      try {
        const result = await sendBackground(agentId, observationMsg)
        setCooldown(agentId)
        const response = result?.output || ''

        if (response.includes('[NO_CHANGES]')) {
          emitEvent({
            type: 'observation_no_change',
            agentId,
            title: `${AGENT_LABELS[agentId] || agentId} -- no changes needed`,
          })
          continue
        }

        // Extract [SUGGEST_EDIT:tab] block
        const suggestMatch = response.match(/\[SUGGEST_EDIT:(hpi|pe|ap)\]\s*([\s\S]*)/i)
        if (!suggestMatch) {
          emitEvent({
            type: 'observation_no_change',
            agentId,
            title: `${AGENT_LABELS[agentId] || agentId} -- no actionable suggestion`,
          })
          continue
        }

        const suggestedLegacyTab = suggestMatch[1].toLowerCase()
        const suggestedContent = suggestMatch[2].trim()
        const suggestedCanvasTab = (suggestedLegacyTab === 'ap' ? 'assessment_and_plan' : suggestedLegacyTab) as CanvasTab

        emitEvent({
          type: 'suggestion_proposed',
          agentId,
          title: `${AGENT_LABELS[agentId] || agentId} proposed edits to ${suggestedLegacyTab.toUpperCase()}`,
        })

        // Route to Casey for review
        emitEvent({
          type: 'suggestion_casey_review',
          agentId,
          targetAgentId: 'casey',
          title: `Casey reviewing ${suggestedLegacyTab.toUpperCase()} suggestion from ${AGENT_LABELS[agentId] || agentId}`,
        })

        const currentContent = stripHtml(canvasRef.current.tabs[suggestedCanvasTab].html)
        const reviewMsg =
          `[REVIEW_SUGGESTION] Agent: ${agentId}\nTab: ${suggestedLegacyTab.toUpperCase()}\n\n` +
          `Current canvas content:\n${currentContent}\n\n` +
          `Proposed edit:\n${suggestedContent}\n\n` +
          `Please review for clinical accuracy and coherence. Respond with [APPROVE_SUGGESTION], ` +
          `[REJECT_SUGGESTION] with rationale, or [REFINE_SUGGESTION] with your improved version.`

        const caseyResult = await sendBackground('casey', reviewMsg)
        const caseyResponse = caseyResult?.output || ''

        if (caseyResponse.includes('[REJECT_SUGGESTION]')) {
          emitEvent({
            type: 'suggestion_rejected',
            agentId: 'casey',
            targetAgentId: agentId,
            title: `Casey rejected ${suggestedLegacyTab.toUpperCase()} suggestion`,
            detail: caseyResponse.replace('[REJECT_SUGGESTION]', '').trim().substring(0, 200),
          })
          continue
        }

        // Accept or approve — surface to user
        let approvedContent = suggestedContent
        if (caseyResponse.includes('[APPROVE_SUGGESTION]')) {
          const afterApproval = caseyResponse.split('[APPROVE_SUGGESTION]')[1]?.trim()
          if (afterApproval && afterApproval.length > 50) {
            approvedContent = afterApproval
          }
        }

        canvasRef.current.proposeSuggestion(suggestedCanvasTab, approvedContent)

        setPendingSuggestions(prev => ({
          ...prev,
          [agentId]: { tab: suggestedCanvasTab, status: 'pending' },
        }))

        emitEvent({
          type: 'suggestion_approved',
          agentId,
          title: `${suggestedLegacyTab.toUpperCase()} suggestion surfaced to physician`,
        })
      } catch (err) {
        console.error(`[Observer] Error dispatching to ${agentId}:`, err)
        emitEvent({
          type: 'observation_no_change',
          agentId,
          title: `Observation dispatch to ${agentId} failed`,
          detail: err instanceof Error ? err.message : 'Unknown error',
        })
      }
    }

    setIsDispatching(false)
  }, [sendBackground, isOnCooldown, setCooldown, getCanvasSnapshots, emitEvent])

  /**
   * Record a clinically significant event.
   * When buffer reaches threshold or debounce timer fires, dispatches automatically.
   */
  const recordEvent = useCallback((event: ObservableEvent) => {
    eventBuffer.current.push(event)

    // Clear existing debounce timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
    }

    // If buffer is full, dispatch immediately
    if (eventBuffer.current.length >= bufferThreshold) {
      const events = [...eventBuffer.current]
      eventBuffer.current = []
      dispatchObservations(events)
      return
    }

    // Set debounce timer
    debounceTimer.current = setTimeout(() => {
      if (eventBuffer.current.length > 0) {
        const events = [...eventBuffer.current]
        eventBuffer.current = []
        dispatchObservations(events)
      }
      debounceTimer.current = null
    }, debounceMs)
  }, [bufferThreshold, debounceMs, dispatchObservations])

  /**
   * Trigger manual observation — flush buffer and dispatch immediately.
   */
  const triggerManual = useCallback(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current)
      debounceTimer.current = null
    }
    if (eventBuffer.current.length > 0) {
      const events = [...eventBuffer.current]
      eventBuffer.current = []
      dispatchObservations(events)
    }
  }, [dispatchObservations])

  /**
   * Clear a pending suggestion for an agent (after user resolves all hunks).
   */
  const clearPendingSuggestion = useCallback((agentId: string) => {
    setPendingSuggestions(prev => {
      const next = { ...prev }
      delete next[agentId]
      return next
    })
  }, [])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current)
      }
    }
  }, [])

  return {
    recordEvent,
    triggerManual,
    pendingSuggestions,
    clearPendingSuggestion,
    isDispatching,
    bufferSize: eventBuffer.current.length,
  }
}
